import { Component, Input, OnInit } from '@angular/core'
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms'
import { SelectItem } from 'primeng/api'
import { CronOptions } from '../../models/cron-options'

@Component({
  selector: 'jml-lib-cron-editor',
  templateUrl: './cron-editor.component.html',
  styleUrls: ['./cron-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CronEditorComponent
    }
  ]
})
export class CronEditorComponent implements OnInit, ControlValueAccessor {
  @Input() options: CronOptions = {
    defaultTime: '10:00:00',
    use24HourTime: true,

    hideMinutesTab: false,
    hideHourlyTab: false,
    hideDailyTab: false,
    hideWeeklyTab: false,
    hideMonthlyTab: false,
    hideYearlyTab: false,
    hideAdvancedTab: false,

    hideSeconds: false
  }

  public activeTab: number = 0
  public selectOptions = this.getSelectOptions()

  // TODO: make this actually do something
  private disabled = false

  cronForm: FormControl

  minutesForm: FormGroup
  hourlyForm: FormGroup
  dailyForm: FormGroup
  weeklyForm: FormGroup
  monthlyForm: FormGroup
  yearlyForm: FormGroup
  advancedForm: FormGroup

  constructor(private fb: FormBuilder) {}

  /* Update the cron output to that of the selected tab.
   * The cron output value is updated whenever a form is updated. To make it change in response to tab selection, we simply reset
   * the value of the form that goes into focus. */
  public onTabFocus(event) {
    const idx = event.index
    switch (idx) {
      case 0:
        this.minutesForm.setValue(this.minutesForm.value)
        break
      case 1:
        this.hourlyForm.setValue(this.hourlyForm.value)
        break
      case 2:
        this.dailyForm.setValue(this.dailyForm.value)
        break
      case 3:
        this.weeklyForm.setValue(this.weeklyForm.value)
        break
      case 4:
        this.monthlyForm.setValue(this.monthlyForm.value)
        break
      case 5:
        this.yearlyForm.setValue(this.yearlyForm.value)
        break
      case 6:
        this.advancedForm.setValue(this.advancedForm.value)
        break
      default:
        throw new Error('Invalid tab selected')
    }
  }

  ngOnInit(): void {
    this.cronForm = new FormControl('* 0 0 ? * *')

    this.cronForm.valueChanges.subscribe((cron) => this.onChange(cron))

    const [defaultHours, defaultMinutes, defaultSeconds] = this.options.defaultTime.split(':').map(Number)

    this.minutesForm = this.fb.group({
      hours: [0],
      minutes: [1],
      seconds: [0]
    })
    this.minutesForm.valueChanges.subscribe((value) => this.computeMinutesCron(value))

    this.hourlyForm = this.fb.group({
      hours: [1],
      minutes: [0],
      seconds: [0]
    })
    this.hourlyForm.valueChanges.subscribe((value) => this.computeHourlyCron(value))

    this.dailyForm = this.fb.group({
      subTab: ['everyDays'],
      everyDays: this.fb.group({
        days: [1],
        hours: [this.getAmPmHour(1)],
        minutes: [0],
        seconds: [0],
        hourType: [this.getHourType(0)]
      }),
      everyWeekDay: this.fb.group({
        days: [0],
        hours: [this.getAmPmHour(1)],
        minutes: [0],
        seconds: [0],
        hourType: [this.getHourType(0)]
      })
    })
    this.dailyForm.valueChanges.subscribe((value) => this.computeDailyCron(value))

    this.weeklyForm = this.fb.group({
      MON: [true],
      TUE: [false],
      WED: [false],
      THU: [false],
      FRI: [false],
      SAT: [false],
      SUN: [false],
      hours: [this.getAmPmHour(defaultHours)],
      minutes: [defaultMinutes],
      seconds: [defaultSeconds],
      hourType: [this.getHourType(defaultHours)]
    })
    this.weeklyForm.valueChanges.subscribe((next) => this.computeWeeklyCron(next))

    this.monthlyForm = this.fb.group({
      subTab: ['specificDay'],
      specificDay: this.fb.group({
        day: [1],
        months: [1],
        hours: [this.getAmPmHour(defaultHours)],
        minutes: [defaultMinutes],
        seconds: [defaultSeconds],
        hourType: [this.getHourType(defaultHours)]
      }),
      specificWeekDay: this.fb.group({
        monthWeek: ['#1'],
        day: ['MON'],
        months: [1],
        hours: [this.getAmPmHour(defaultHours)],
        minutes: [defaultMinutes],
        seconds: [defaultSeconds],
        hourType: [this.getHourType(defaultHours)]
      })
    })
    this.monthlyForm.valueChanges.subscribe((next) => this.computeMonthlyCron(next))

    this.yearlyForm = this.fb.group({
      subTab: ['specificMonthDay'],
      specificMonthDay: this.fb.group({
        month: [1],
        day: ['1'],
        hours: [this.getAmPmHour(defaultHours)],
        minutes: [defaultMinutes],
        seconds: [defaultSeconds],
        hourType: [this.getHourType(defaultHours)]
      }),
      specificMonthWeek: this.fb.group({
        monthWeek: ['#1'],
        day: ['MON'],
        month: [1],
        hours: [this.getAmPmHour(defaultHours)],
        minutes: [defaultMinutes],
        seconds: [defaultSeconds],
        hourType: [this.getHourType(defaultHours)]
      })
    })
    this.yearlyForm.valueChanges.subscribe((next) => this.computeYearlyCron(next))

    this.advancedForm = this.fb.group({
      expression: ['0 15 10 L-2 * ?']
    })
    this.advancedForm.controls.expression.valueChanges.subscribe((next) => this.computeAdvancedExpression(next))

    // radio button bug in primeng... remove this when primeng is version 11 or bigger
    // https://github.com/primefaces/primeng/issues/9162
    ;[this.dailyForm, this.monthlyForm, this.yearlyForm].forEach((form) => {
      form.controls.subTab.valueChanges.subscribe((e) => {
        form.controls.subTab.setValue(e, { emitEvent: false })
      })
    })
  }

  private computeMinutesCron(state: any) {
    const cron = `${state.seconds} 0/${state.minutes} * * * ?`.trim()
    this.cronForm.setValue(cron)
  }

  private computeHourlyCron(state: any) {
    const cron = `${state.seconds} ${state.minutes} 0/${state.hours} * * ?`.trim()
    this.cronForm.setValue(cron)
  }

  private computeDailyCron(state: any) {
    let cron = ''
    switch (state.subTab) {
      case 'everyDays':
        cron =
          `${state.everyDays.seconds} ${state.everyDays.minutes} ${this.hourToCron(state.everyDays.hours, state.everyDays.hourType)} 1/${state.everyDays.days} * ?`.trim()
        break
      case 'everyWeekDay':
        cron =
          `${state.everyWeekDay.seconds} ${state.everyWeekDay.minutes} ${this.hourToCron(state.everyWeekDay.hours, state.everyWeekDay.hourType)} ? * MON-FRI`.trim()
        break
      default:
        throw new Error('Invalid cron daily subtab selection')
    }
    this.cronForm.setValue(cron)
  }

  private computeWeeklyCron(state: any) {
    const days = this.selectOptions.days
      .reduce((acc, day) => (state[day.value] ? acc.concat([day.value]) : acc), [])
      .join(',')
    const cron = `${state.seconds} ${state.minutes} ${this.hourToCron(state.hours, state.hourType)} ? * ${days}`.trim()
    this.cronForm.setValue(cron)
  }

  private computeMonthlyCron(state: any) {
    let cron = ''
    switch (state.subTab) {
      case 'specificDay':
        cron =
          `${state.specificDay.seconds} ${state.specificDay.minutes} ${this.hourToCron(state.specificDay.hours, state.specificDay.hourType)} ${state.specificDay.day} 1/${state.specificDay.months} ?`.trim()
        break
      case 'specificWeekDay':
        cron =
          `${state.specificWeekDay.seconds} ${state.specificWeekDay.minutes} ${this.hourToCron(state.specificWeekDay.hours, state.specificWeekDay.hourType)} ? 1/${state.specificWeekDay.months} ${state.specificWeekDay.day}${state.specificWeekDay.monthWeek}`.trim()
        break
      default:
        throw new Error('Invalid cron monthly subtab selection')
    }
    this.cronForm.setValue(cron)
  }

  private computeYearlyCron(state: any) {
    let cron = ''
    switch (state.subTab) {
      case 'specificMonthDay':
        cron =
          `${state.specificMonthDay.seconds} ${state.specificMonthDay.minutes} ${this.hourToCron(state.specificMonthDay.hours, state.specificMonthDay.hourType)} ${state.specificMonthDay.day} ${state.specificMonthDay.month} ?`.trim()
        break
      case 'specificMonthWeek':
        cron =
          `${state.specificMonthWeek.seconds} ${state.specificMonthWeek.minutes} ${this.hourToCron(state.specificMonthWeek.hours, state.specificMonthWeek.hourType)} ? ${state.specificMonthWeek.month} ${state.specificMonthWeek.day}${state.specificMonthWeek.monthWeek}`.trim()
        break
      default:
        throw new Error('Invalid cron yearly subtab selection')
    }
    this.cronForm.setValue(cron)
  }

  private computeAdvancedExpression(expression: any) {
    let cron = expression
    this.cronForm.setValue(cron)
  }

  private getAmPmHour(hour: number) {
    return this.options.use24HourTime ? hour : ((hour + 11) % 12) + 1
  }

  private getHourType(hour: number) {
    return this.options.use24HourTime ? null : hour >= 12 ? 'PM' : 'AM'
  }

  private hourToCron(hour: number, hourType: string) {
    if (this.options.use24HourTime) {
      return hour
    } else {
      return hourType === 'AM' ? (hour === 12 ? 0 : hour) : hour === 12 ? 12 : hour + 12
    }
  }

  private handleModelChange(cron: string) {
    if (!cron) {
      cron = '* 0 0 ? * *'
    }

    if (!this.cronIsValid(cron)) {
      throw new Error('Invalid cron expression, there must be 6 segments')
    }

    const origCron: string = cron

    const [seconds, minutes, hours, dayOfMonth, month, dayOfWeek] = cron.split(' ')

    if (cron.match(/\d+ 0\/\d+ \* \* \* [\?\*]/)) {
      const controls = this.minutesForm.controls
      this.activeTab = 0
      controls['minutes'].setValue(parseInt(minutes.substring(2), 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ 0\/\d+ \* \* [\?\*]/)) {
      const controls = this.hourlyForm.controls
      this.activeTab = 1

      controls['hours'].setValue(parseInt(hours.substring(2), 10))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ \d+ 1\/\d+ \* [\?\*]/)) {
      const controls = (<FormGroup>this.dailyForm.controls['everyDays']).controls
      this.activeTab = 2

      this.dailyForm.controls['subTab'].setValue('everyDays')
      controls['days'].setValue(parseInt(dayOfMonth.substring(2), 10))
      const parsedHours = parseInt(hours, 10)
      controls['hours'].setValue(this.getAmPmHour(parsedHours))
      controls['hourType'].setValue(this.getHourType(parsedHours))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ \d+ [\?\*] \* MON-FRI/)) {
      const controls = (<FormGroup>this.dailyForm.controls['everyWeekDay']).controls
      this.activeTab = 2

      this.dailyForm.controls['subTab'].setValue('everyWeekDay')
      const parsedHours = parseInt(hours, 10)
      controls['hours'].setValue(this.getAmPmHour(parsedHours))
      controls['hourType'].setValue(this.getHourType(parsedHours))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ \d+ [\?\*] \* (MON|TUE|WED|THU|FRI|SAT|SUN)(,(MON|TUE|WED|THU|FRI|SAT|SUN))*/)) {
      const controls = this.weeklyForm.controls
      this.activeTab = 3
      this.selectOptions.days.forEach((weekDay) => controls[weekDay.value].setValue(false))
      dayOfWeek.split(',').forEach((weekDay) => controls[weekDay].setValue(true))
      const parsedHours = parseInt(hours, 10)
      controls['hours'].setValue(this.getAmPmHour(parsedHours))
      controls['hourType'].setValue(this.getHourType(parsedHours))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ \d+ (\d+|L|LW|1W) 1\/\d+ [\?\*]/)) {
      const controls = (<FormGroup>this.monthlyForm.controls['specificDay']).controls
      this.activeTab = 4

      this.monthlyForm.controls['subTab'].setValue('specificDay')
      controls['day'].setValue(dayOfMonth)
      controls['months'].setValue(parseInt(month.substring(2), 10))
      const parsedHours = parseInt(hours, 10)
      controls['hours'].setValue(this.getAmPmHour(parsedHours))
      controls['hourType'].setValue(this.getHourType(parsedHours))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ \d+ [\?\*] 1\/\d+ (MON|TUE|WED|THU|FRI|SAT|SUN)((#[1-5])|L)/)) {
      const controls = (<FormGroup>this.monthlyForm.controls['specificWeekDay']).controls
      const day = dayOfWeek.substring(0, 3)
      const monthWeek = dayOfWeek.substring(3)
      this.activeTab = 4
      this.monthlyForm.controls['subTab'].setValue('specificWeekDay')
      controls['monthWeek'].setValue(monthWeek)
      controls['day'].setValue(day)
      controls['months'].setValue(parseInt(month.substring(2), 10))
      const parsedHours = parseInt(hours, 10)
      controls['hours'].setValue(this.getAmPmHour(parsedHours))
      controls['hourType'].setValue(this.getHourType(parsedHours))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ \d+ (\d+|L|LW|1W) \d+ [\?\*]/)) {
      const controls = (<FormGroup>this.yearlyForm.controls['specificMonthDay']).controls
      this.activeTab = 5
      this.yearlyForm.controls['subTab'].setValue('specificMonthDay')
      controls['month'].setValue(parseInt(month, 10))
      controls['day'].setValue(dayOfMonth)
      const parsedHours = parseInt(hours, 10)
      controls['hours'].setValue(this.getAmPmHour(parsedHours))
      controls['hourType'].setValue(this.getHourType(parsedHours))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else if (cron.match(/\d+ \d+ \d+ [\?\*] \d+ (MON|TUE|WED|THU|FRI|SAT|SUN)((#[1-5])|L)/)) {
      const controls = (<FormGroup>this.yearlyForm.controls['specificMonthWeek']).controls
      const day = dayOfWeek.substring(0, 3)
      const monthWeek = dayOfWeek.substring(3)
      this.activeTab = 5
      this.yearlyForm.controls['subTab'].setValue('specificMonthWeek')
      controls['monthWeek'].setValue(monthWeek)
      controls['day'].setValue(day)
      controls['month'].setValue(parseInt(month, 10))
      const parsedHours = parseInt(hours, 10)
      controls['hours'].setValue(this.getAmPmHour(parsedHours))
      controls['hourType'].setValue(this.getHourType(parsedHours))
      controls['minutes'].setValue(parseInt(minutes, 10))
      controls['seconds'].setValue(parseInt(seconds, 10))
    } else {
      this.activeTab = 6
      this.advancedForm.controls['expression'].setValue(origCron)
    }
  }

  private cronIsValid(cron: string): boolean {
    if (cron) {
      const cronParts = cron.split(' ')
      return cronParts.length === 6
    }

    return false
  }

  private getOrdinalSuffix(value: string) {
    if (value.length > 1) {
      const secondToLastDigit = value.charAt(value.length - 2)
      if (secondToLastDigit === '1') {
        return 'th'
      }
    }

    const lastDigit = value.charAt(value.length - 1)
    switch (lastDigit) {
      case '1':
        return 'st'
      case '2':
        return 'nd'
      case '3':
        return 'rd'
      default:
        return 'th'
    }
  }

  private getSelectOptions() {
    return {
      months: this.getRange(1, 12),
      monthWeeks: [
        { label: 'First', value: '#1' },
        { label: 'Second', value: '#2' },
        { label: 'Third', value: '#3' },
        { label: 'Fourth', value: '#4' },
        { label: 'Fifth', value: '#5' },
        { label: 'Last', value: 'L' }
      ],
      days: [
        { label: 'Monday', value: 'MON' },
        { label: 'Tuesday', value: 'TUE' },
        { label: 'Wednesday', value: 'WED' },
        { label: 'Thursday', value: 'THU' },
        { label: 'Friday', value: 'FRI' },
        { label: 'Saturday', value: 'SAT' },
        { label: 'Sunday', value: 'SUN' }
      ],
      minutes: this.getRange(0, 59),
      fullMinutes: this.getRange(0, 59),
      seconds: this.getRange(0, 59),
      hours: this.getRange(1, 23),
      monthDays: this.getRange(1, 31),
      monthDaysWithLasts: [
        { label: 'First Weekday', value: '1W' },
        ...[
          ...this.getRange(1, 31).map((item) => {
            return { label: item.label + this.getOrdinalSuffix(item.label), value: `${item.value}` }
          }),
          { label: 'Last Weekday', value: 'LW' },
          { label: 'Last Day', value: 'L' }
        ]
      ],
      monthDaysWithOutLasts: this.getRange(1, 31).map((item) => {
        return { label: item.label + this.getOrdinalSuffix(item.label), value: `${item.value}` }
      }),
      hourTypes: ['AM', 'PM']
    }
  }

  private getRange(start: number, end: number): SelectItem[] {
    if (start > end) return []
    return [...Array(end - start + 1).keys()].map((n) => {
      return { label: `${n + start}`, value: n + start }
    })
  }

  /*
   * ControlValueAccessor
   */
  onChange = (_: any) => {}
  onTouched = () => {}

  writeValue(obj: string): void {
    this.cronForm.setValue(obj)
    this.handleModelChange(obj)
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }
}
